import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-35" data-mp="217k7hlu9cinz">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-36" data-mp="1wbwk9bzc5zl4">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-37" data-mp="1id5ag1rib02s">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-38" data-mp="2eicklk7cec24">
              <div className="flex items-center gap-3" data-id="element-39" data-mp="1lu8dqvd1bsxu">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-40" data-mp="1lv07e0n2nglh">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-41" data-mp="1qs9vncyq5m7q" />
                </div>
                <div data-id="element-42" data-mp="1uvy7i7ue33ys">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-43" data-mp="1ut25a7bidt4j">
                    Snakker · LEO Pharma
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-44" data-mp="3690kksxa5be7">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-45" data-mp="125fk6xjncjm0">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-46" data-mp="1b7ylhd7sao65" />
              </button>
            </div>

            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-47" data-mp="nhuin82f6sg7">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-48" data-mp="jdwf8l62avhc">
                Think global, act local:{' '}
                <span className="text-[#6C2BD9]" data-id="element-49" data-mp="sg5nt5hrses3">
                  one campaign engine, many local executions
                </span>{' '}
                — Snakker centralizes the campaign complexity so LEO's markets
                can move faster, and gives HQ full visibility across every
                brand, market and campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-50" data-mp="2bd900e031oex">
                The Pressure Is Rising
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-51" data-mp="22fqb2yjbipxr">
                More brands, more markets, more channels, more campaigns, more
                competition. The result is{' '}
                <strong data-id="element-52" data-mp="2v32xn8phaxr2">more complexity for pharma</strong> and{' '}
                <strong data-id="element-53" data-mp="2z4xxs9vob539">more fatigue for HCPs</strong>. HCPs don't lack
                information — they lack time. What cuts through is short and
                relevant.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-54" data-mp="2l7j2wpmni53w">
                The Hidden Legacy Potential
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-55" data-mp="19p86tu6uyiir">
                Every product moves through <strong data-id="element-56" data-mp="1eq4fel25msui">R&amp;D</strong>,{' '}
                <strong data-id="element-57" data-mp="1emqbh8duebky">Launch</strong>, <strong data-id="element-58" data-mp="1tz9ftc9fmz94">Mature</strong> and{' '}
                <strong data-id="element-59" data-mp="1u010hs1hrckm">Legacy</strong>. Commercial attention concentrates on
                the newest assets, while the established portfolio quietly
                carries the volume. The strategic move is to{' '}
                <em data-id="element-60" data-mp="1l4bgnnfoyq9c">soften the cliff and extend the tail</em> — where small
                improvements compound into significant portfolio value.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-61" data-mp="2cwap39a0rliv">
                LEO's Revenue Mix Today
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-62" data-mp="23wnkyfpwo1m4">
                LEO delivered <strong data-id="element-63" data-mp="23yqjxp91d52z">DKK 13.5bn</strong> revenue in 2025.{' '}
                <strong data-id="element-64" data-mp="1yx19obtn0vfy">
                  Roughly 76% comes from brands past exclusivity
                </strong>{' '}
                — Established dermatology (DKK 8.0bn, +2% CER) plus Critical
                Care (DKK 2.3bn) — while Strategic brands (DKK 3.0bn, +48% CER)
                take nearly all the commercial investment. That established base
                funds innovation, spans{' '}
                <strong data-id="element-65" data-mp="iebpkchm09qk">25+ promoted brands across 70+ markets</strong> (31
                affiliates), and is the part of the business that can least
                afford traditional agency campaigns.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-66" data-mp="s5pxn8nmmhvb">
                The Value of Small Improvements
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-67" data-mp="11306yogvvwxl">
                Applied to LEO's own numbers, even a{' '}
                <strong data-id="element-68" data-mp="164602neghm7n">0.5–5% improvement</strong> across the established
                portfolio is worth tens to hundreds of millions of kroner per
                year. At DKK 13.5bn revenue and a 76% established share, a{' '}
                <strong data-id="element-69" data-mp="3ctuc3ffc3qi8">1% improvement equals roughly DKK 103m annually</strong>{' '}
                — value that is currently unmanaged and under-optimized across
                25+ brands and 70+ markets.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-70" data-mp="2zuv3de3sqw9p">
                Relevance Is Local — And Time Is the Enemy
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-71" data-mp="39sivrxdxjrop">
                Markets need content that reflects local realities, priorities
                and HCP needs. Global delivery is often too slow, too generic
                and too far removed from what local teams need now. The result
                is a{' '}
                <strong data-id="element-72" data-mp="1bebvju2fah2">
                  growing gap between global campaign development and local
                  market execution
                </strong>{' '}
                — and it widens with every new brand, market and campaign cycle.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-73" data-mp="6bixfo9tyve5">
                Production Was Solved. Deployment Was Not.
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-74" data-mp="oxqau1xhkjdv">
                The <strong data-id="element-75" data-mp="txmj4dd7uwz8">agency model</strong> works for a handful of major
                campaigns, but at scale it is too expensive for high volumes,
                too slow for frequent local adaptation, too fragmented across
                brands and markets and too hard to measure consistently across
                countries. LEO's <strong data-id="element-76" data-mp="678n6brigdzd">internalized model</strong> solved
                creative supply, speed of production and cost per asset. What it
                does not solve is getting that content live and engaging:
                capacity to run campaigns in 20+ markets, attention for legacy
                and low-priority brands, per-market adaptation at campaign
                speed, continuous optimization after launch, and execution that
                does not consume affiliate hours. Master content that never goes live in fifteen
                markets creates no impact.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-77" data-mp="7ph9eh5m05qg">
                No Doctor Has Time for a Three-Course Meal
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-78" data-mp="3uuq5dfu0u07w">
                HCPs do not lack information — they lack time. A 40-slide
                approved deck, a 12-page reprint, a 60-minute webinar or an
                8-minute e-detailer gets opened, skimmed, saved for later and
                never returned to. Not even the French have time for three
                courses any more. A <strong data-id="element-79" data-mp="3b93kd4bgi7q3">Snak</strong> is 60–90 seconds, one
                message, one clear action, interactive and mobile-first —
                finished on the ward rather than saved for later. Critically,{' '}
                <strong data-id="element-80" data-mp="326ub0hrxckea">short is not less</strong>: same claims, same science,{' '}
                <strong data-id="element-81" data-mp="2x9u301bil7kj">distilled rather than diluted</strong>, with every second
                earning the next. That is the quality bar behind the word
                Snakkerfy.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-82" data-mp="1ho9zww1tqd49">
                Where Snakker Fits
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-83" data-mp="1qpzs443j5g33">
                Snakker is the <strong data-id="element-84" data-mp="1qo5e79qoeo8i">Snakkerfy Layer</strong> — the layer that
                turns approved content into short, relevant, interactive
                engagement and gets it live in every market. It sits adjacent to
                LEO's production team, not in place of it, and it is not
                involved in upstream creative development. LEO's creative and
                production teams continue to own approved content, claims and
                global MLR approval. Snakker Snakkerfies that content, localizes
                it inside the Snak platform, runs campaign logic and live
                execution, and closes the insight and optimization loop. LEO's
                production team continues to publish into LEO-owned channels —
                website, email and IVA. We improve the path from{' '}
                <strong data-id="element-85" data-mp="2k99jhjmaoyp7">
                  approved content → Snakkerfied master → localized campaign →
                  insight loop
                </strong>
                .
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-86" data-mp="1siv7xu1r7ygp">
                The Real Constraint — Legacy Brands and Local Capacity
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-87" data-mp="21jjpuzu9qiyv">
                Affiliates prioritise launch and growth brands. Legacy and
                off-patent brands — which still carry meaningful revenue — get
                whatever time is left over, which in most markets is none. The
                constraint is not relevance or personalization; it is{' '}
                <strong data-id="element-88" data-mp="1drmdn7mwpox8">local resource scarcity</strong>. Low-attention brands
                need execution that requires{' '}
                <strong data-id="element-89" data-mp="11bcxt2dn2lgj">no affiliate effort at all</strong>.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-90" data-mp="1190m36yh57bx">
                Why Snakker
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-91" data-mp="sb6rc0gcylqv">
                Snakker was born to solve exactly this. Thirty years of pharma
                campaign models turned into AI workflows: storyboarding and
                creation in minutes rather than months, producing short,
                engaging, TA-relevant campaigns ready for deployment. We combine{' '}
                <strong data-id="element-92" data-mp="8mpkpu31n2e2">deep pharma fluency</strong> in regulated environments,{' '}
                <strong data-id="element-93" data-mp="4kujsqp0yr0t">a proprietary interactive engagement platform</strong>,{' '}
                <strong data-id="element-94" data-mp="4mx5iyj59xzq">local distribution capability</strong> and a{' '}
                <strong data-id="element-95" data-mp="3vsgkcagg6ddg">closed behavioural feedback loop</strong> — with master
                agreements already in place across 15 of the top 30 healthcare
                companies globally.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-96" data-mp="3q6fy313rlwiw">
                You Have to Centralize to Localize
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-97" data-mp="3h5hqy9y158w3">
                <strong data-id="element-98" data-mp="8omxtm71b2v7">Centralize</strong> what should not be rebuilt in every
                market: campaign strategy, scientific narrative, core claims,
                brand standards, reusable assets, production workflows,
                compliance governance, measurement standards and performance
                learning. <strong data-id="element-99" data-mp="3xdais8ykbmh">Decentralize</strong> what only markets can
                own: campaign activation, local insight, language, cultural
                context, channel selection, market-specific emphasis, local
                calls to action and local compliance governance. Decentralized
                does not mean handed to the affiliate:{' '}
                <strong data-id="element-100" data-mp="2h2iczefjav02">Snakker executes the local layer on their behalf</strong>{' '}
                — the affiliate keeps the decisions and loses the workload.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-101" data-mp="2pweycr0vk3ef">
                One Campaign Engine, Many Local Executions
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-102" data-mp="1y6s7hh8e7ghf">
                Snakker becomes the <strong data-id="element-103" data-mp="1t777r4lef43n">central campaign office</strong>. We
                build the master campaign — strategy, hook, email and banner
                assets, teaser Snaks, trailer Snaks, calls to action,
                measurement framework and optimization recommendations. Local
                teams then adapt and activate it for their market. The result:{' '}
                <strong data-id="element-104" data-mp="3inf3x6eutf87">
                  global consistency, local relevance, faster execution
                </strong>
                .
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-105" data-mp="csl6zxmsfa8b">
                A New Category: Campaign Service Management
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-106" data-mp="mp7qahzgtt88">
                Snakker is not an agency and not a SaaS licence. It is{' '}
                <strong data-id="element-107" data-mp="rpviv83cei4k">Campaign Service Management</strong> — managed pharma
                campaign execution powered by software economics. We deliver the
                outcome, not the tool.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-108" data-mp="10mw10wztjjzk">
                The Operating Model — A Loop, Not a Project
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-109" data-mp="1aea7joq6tk27">
                Four steps that feed each other, cycle after cycle:{' '}
                <strong data-id="element-110" data-mp="1jfq9r96umfvc">leverage existing approved assets</strong>,{' '}
                <strong data-id="element-111" data-mp="1jdnk4q9lyor5">deploy rapidly in local markets</strong>,{' '}
                <strong data-id="element-112" data-mp="33yk9of69co9h">capture behavioural data</strong> on real HCP
                engagement, and <strong data-id="element-113" data-mp="2zvxkyn4vvjgi">optimize continuously</strong> so every
                interaction sharpens the next campaign — and every market learns
                from the others.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-114" data-mp="2pyt0qsully2c">
                HQ Gets the Control Tower
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-115" data-mp="3nnkze5kyl21w">
                A cross-brand, cross-region dashboard for every Snak and
                campaign in LEO: <strong data-id="element-116" data-mp="3dunlydutq3a5">what is live</strong>,{' '}
                <strong data-id="element-117" data-mp="38wvjipwjyyrp">where</strong>, <strong data-id="element-118" data-mp="38xdoqc170nbr">what it looks like</strong>,{' '}
                <strong data-id="element-119" data-mp="38urfm7ynlzrf">how it performs</strong>,{' '}
                <strong data-id="element-120" data-mp="a7x0ycotybbx">which markets outperform</strong>,{' '}
                <strong data-id="element-121" data-mp="5g59lzxuo1kq">what should improve</strong> and{' '}
                <strong data-id="element-122" data-mp="5ekfgbfg6kne">what can be reused as best practice</strong> — plus
                on/off control per campaign. More control for HQ, more speed for
                local markets.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-123" data-mp="23osx7kx12o70">
                Turn-Key — Not a DIY Project
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-124" data-mp="1uwhbudgrtqxw">
                Affiliates and partners receive a{' '}
                <strong data-id="element-125" data-mp="1fz8g77kfq10d">finished campaign, ready to go live</strong>. Snakker
                Snakkerfies the approved content, localizes it per market, builds
                the campaign logic, publishes and distributes it, tracks
                engagement and reports back with optimizations. The affiliate
                does exactly two things:{' '}
                <strong data-id="element-126" data-mp="31dm8ytcb8u12">reviews and signs off, then says go</strong> — no brief
                to write, no local project, no vendor to onboard and no hours to
                find. Campaigns are built from approved content, turned into
                short interactive <strong data-id="element-127" data-mp="3ggw51mwgog8q">Snaks™</strong>, published across
                channels, measured on every interaction and improved campaign
                after campaign. The same format adapts across the HCP journey —
                awareness, education, engagement, and conversion &amp; loyalty.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-128" data-mp="3kfv3conoter7">
                Governance — Who Decides, Executes and Approves
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-129" data-mp="3u79czp9cruy2">
                Existing approved content stays with LEO's production team under
                global MLR. The <strong data-id="element-130" data-mp="8016gjmp5i0g">Master Snak</strong> is decided globally
                and executed by Snakker. In <strong data-id="element-131" data-mp="2dszkqk1zpm1y">key markets</strong>, the
                local affiliate briefs and signs off its own campaigns — the
                model they run today — while Snakker executes. In{' '}
                <strong data-id="element-132" data-mp="2yd47o8imd5fu">secondary and legacy markets</strong>, the global brand
                team decides the scope, Snakker executes end to end, and the
                affiliate only signs off — no local project, no local
                resourcing. Deployment into LEO-owned channels stays with LEO's
                production team. MLR and local approval gates do not move;
                Snakker executes inside them.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-133" data-mp="2ffkuqtt1j2o5">
                Fast Marketing™ Economics
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-134" data-mp="2oe6gsyxq0zto">
                Cost is built from three transparent units that improve with
                volume: a <strong data-id="element-135" data-mp="2tf2jj96c6fxi">Master Snak from €2,900 down to €1,900</strong>{' '}
                as campaign volume scales, <strong data-id="element-136" data-mp="3qjupdpk1l9e">country localization</strong>{' '}
                from €999 with up to 50% volume benefit, and{' '}
                <strong data-id="element-137" data-mp="irh5pl8nigzs">country activation and insights</strong> from €99 per
                month per country on the same curve. A traditional digital
                campaign for one brand in one country costs{' '}
                <strong data-id="element-138" data-mp="915q31flujd2">€15,000–€19,000</strong>; the Snakker model delivers a
                fully managed localized deployment in the{' '}
                <strong data-id="element-139" data-mp="4zkhvte0mt97">low thousands</strong> — and scales predictably from one
                brand in one market to the full portfolio.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-140" data-mp="3r6r52ll5p83v">
                The Proposition
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-141" data-mp="3m8h76381rtek">
                <strong data-id="element-142" data-mp="3i7nmrjttcwqf">The Snakkerfy Layer</strong> — approved content turned
                short, relevant and interactive.{' '}
                <strong data-id="element-143" data-mp="1kg1fnifsi58e">Local relevance without local effort.</strong>{' '}
                <strong data-id="element-144" data-mp="1ggkakq49za3r">Full HQ visibility.</strong> A partner adjacent to LEO's
                production model — never a replacement for it.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-145" data-mp="3f536p4zr6l5t">
                "But Doesn't This Make Us Dependent?"
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-146" data-mp="3ac08gjk16nja">
                Every operating model depends on capabilities — agencies,
                internal teams, technology, production partners, data providers,
                compliance systems. The question is whether that dependency is{' '}
                <strong data-id="element-147" data-mp="2viydqzl5yq09">
                  transparent, governed, measurable, replaceable, cost-controlled
                </strong>{' '}
                and backed by full access to outputs. LEO receives all campaign
                content, all Snaks and approved assets, all storyboards, all
                local versions, all campaign data, all performance reporting,
                standardized workflows, visible costs, agreed service levels and
                the HQ Control Tower. This is a{' '}
                <strong data-id="element-148" data-mp="s6h99bhb0gmz">managed partnership, not a black box</strong>.
              </p>
              <p className="text-slate-700 leading-relaxed mt-4" data-id="element-149" data-mp="x5bqi8zmhx4z">
                On why execution stays with Snakker: our platform is judged on{' '}
                <strong data-id="element-150" data-mp="16xhj9kxp8il8">impact</strong>, not on assets delivered. Impact depends
                on how campaigns are adapted, deployed and iterated —
                self-service execution inside the platform breaks that loop and
                degrades outcomes. It is not that LEO could not run it;{' '}
                <strong data-id="element-151" data-mp="j6kfivg24rl2">
                  outcomes are simply better when the expert execution loop
                  stays with Snakker
                </strong>
                .
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-152" data-mp="1ulr0sanvqk6k">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-153" data-mp="1yq8hu22uujdn">
                Snakker provides the engine — LEO retains the steering wheel:
                every brand live in every market, including the legacy brands
                nobody locally has time for.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}